import type { AgentDraft } from "@nemo/agent-schema";
import { useCallback, useEffect, useRef, useState } from "react";
import { Api, ApiError, type DriveFolder, type IntegrationState, type Source } from "../api.js";
import { DrivePanel } from "./DrivePanel.js";
import { DriveFolders } from "./DriveFolders.js";
import { faqsFromCsv } from "../csv.js";
import { DocumentsPanel } from "./DocumentsPanel.js";
import { TopicsEditor } from "./TopicsEditor.js";
import { LIMITS, type FieldErrors } from "../draft.js";
import { Badge, Field, Section, TextInput } from "./ui.js";

interface Props {
  api: Api;
  agentId: string;
  form: AgentDraft;
  update: (fn: (f: AgentDraft) => AgentDraft) => void;
  errors: FieldErrors;
  /** Lets the parent know whether training is running (publish waits for it). */
  onSources?: (s: Source[]) => void;
}


const SCHEDULES = [
  { label: "Every day", cron: "0 0 * * *" },
  { label: "Every week", cron: "0 0 * * 1" },
  { label: "Every 6 hours", cron: "0 */6 * * *" },
];
const cadence = (cron: string) => SCHEDULES.find((s) => s.cron === cron)?.label.toLowerCase() ?? "on a schedule";
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : null);

const STATUS: Record<Source["status"], { label: string; tone: "ok" | "warn" | "bad" | "info" | "muted" }> = {
  pending: { label: "Waiting", tone: "info" },
  indexing: { label: "Training…", tone: "info" },
  indexed: { label: "Ready", tone: "ok" },
  error: { label: "Needs attention", tone: "bad" },
  unsupported: { label: "Coming soon", tone: "muted" },
};

export function TrainStep({ api, agentId, form, update, errors, onSources }: Props) {
  const [sources, setSources] = useState<Source[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Google Drive shows up only when the server has the connection switched on (or already holds one).
  const [drive, setDrive] = useState<IntegrationState | null>(null);
  const loadDrive = useCallback(async () => {
    try {
      const list = await api.integrations();
      setDrive(list.find((p) => p.provider === "google_drive") ?? null);
    } catch {
      setDrive(null);
    }
  }, [api]);
  useEffect(() => {
    void loadDrive();
  }, [loadDrive]);
  const driveOn = !!drive && (drive.available || !!drive.connection);
  // Shared folders (docs/GOOGLE-DRIVE-FOLDERS-DESIGN.md): shown only when the server has them on.
  const [folders, setFolders] = useState<{ enabled: boolean; shareWith: string | null; folders: DriveFolder[] } | null>(null);
  const loadFolders = useCallback(async () => {
    try {
      setFolders(await api.driveFolders());
    } catch {
      setFolders(null);
    }
  }, [api]);
  useEffect(() => {
    void loadFolders();
  }, [loadFolders]);

  const refresh = useCallback(async () => {
    try {
      const list = await api.sources(agentId);
      setSources(list);
      onSources?.(list);
      setLoaded(true);
      setFailure(null);
      if (list.some((s) => s.status === "pending" || s.status === "indexing")) timer.current = setTimeout(() => void refresh(), list.length > 100 ? 4000 : 1500);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : "Could not load your sources.");
      timer.current = setTimeout(() => void refresh(), 5000);
    }
  }, [api, agentId, onSources]);

  useEffect(() => {
    void refresh();
    return () => clearTimeout(timer.current);
  }, [refresh]);

  const add = async (body: Record<string, unknown>): Promise<Source | null> => {
    try {
      const s = await api.addSource(agentId, body);
      clearTimeout(timer.current);
      await refresh();
      return s;
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : "That could not be added.");
      return null;
    }
  };
  const remove = async (s: Source) => {
    try {
      await api.removeSource(agentId, s.id);
      setSources((l) => l.filter((x) => x.id !== s.id));
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : "That could not be removed.");
    }
  };
  const retry = async (s: Source) => {
    try {
      await api.reindexSource(agentId, s.id);
      clearTimeout(timer.current);
      await refresh();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : "Could not retry.");
    }
  };

  const of = (...types: string[]) => sources.filter((s) => types.includes(s.type));
  const faqs = form.knowledge?.faqs ?? [];
  const setFaqs = (next: NonNullable<AgentDraft["knowledge"]>["faqs"]) => update((f) => ({ ...f, knowledge: { faqs: next, topics: f.knowledge?.topics ?? [], sources: [] } }));
  const topics = form.knowledge?.topics ?? [];
  const setTopics = (next: NonNullable<AgentDraft["knowledge"]>["topics"]) => update((f) => ({ ...f, knowledge: { faqs: f.knowledge?.faqs ?? [], topics: next, sources: [] } }));

  return (
    <div className="step">
      {notice && (
        <div className="banner warn" role="alert">
          {notice} <button className="btn ghost" onClick={() => setNotice(null)}>Dismiss</button>
        </div>
      )}
      {failure && <div className="banner warn" role="alert">{failure}</div>}

      <OnboardingSection sources={of("onboarding")} add={add} remove={remove} retry={retry} loaded={loaded} />

      <Section title="FAQs" help="Questions and the exact answers you want. Mark a question as a chat initiator to show it as a conversation starter (max 3 in total).">
        <ul className="faqs">
          {faqs.map((f, i) => (
            <li key={i} className="faq">
              <TextInput label={`Question ${i + 1}`} value={f.question} onChange={(v) => setFaqs(faqs.map((x, j) => (j === i ? { ...x, question: v } : x)))} error={errors[`knowledge.faqs.${i}.question`]} />
              <TextInput label={`Answer ${i + 1}`} multiline rows={3} value={f.answer} onChange={(v) => setFaqs(faqs.map((x, j) => (j === i ? { ...x, answer: v } : x)))} error={errors[`knowledge.faqs.${i}.answer`]} />
              <div className="row between">
                <label className="inline">
                  <input type="checkbox" checked={f.chatInitiator ?? false} onChange={(e) => setFaqs(faqs.map((x, j) => (j === i ? { ...x, chatInitiator: e.target.checked } : x)))} /> Chat initiator
                </label>
                <button type="button" className="btn ghost" onClick={() => setFaqs(faqs.filter((_, j) => j !== i))}>
                  Remove question {i + 1}
                </button>
              </div>
              {(!f.question.trim() || !f.answer.trim()) && <p className="hint">Not saved yet: a question needs both a question and an answer.</p>}
            </li>
          ))}
        </ul>
        <div className="row">
          <button type="button" className="btn secondary" disabled={faqs.length >= LIMITS.faqMax} onClick={() => setFaqs([...faqs, { question: "", answer: "", chatInitiator: false }])}>
            Add question
          </button>
          <label className="btn secondary file">
            Import from CSV
            <input
              type="file"
              accept=".csv,text/csv"
              aria-label="Import FAQs from CSV"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                const { faqs: imported, error } = faqsFromCsv(await file.text());
                if (error) return setNotice(`${file.name}: ${error}`);
                const room = LIMITS.faqMax - faqs.length;
                setFaqs([...faqs, ...imported.slice(0, room)]);
                setNotice(imported.length > room ? `Imported ${room} questions; the limit is ${LIMITS.faqMax}.` : `Imported ${imported.length} questions from ${file.name}.`);
              }}
            />
          </label>
          <span className="counter">{faqs.length}/{LIMITS.faqMax}</span>
        </div>
        <p className="help">CSV format: a first row with “question” and “answer” columns (and optionally “chat initiator”).</p>
      </Section>

      <TopicsEditor topics={topics} onChange={setTopics} />

      <DocumentsPanel api={api} agentId={agentId} sources={sources} setSources={setSources} refresh={async () => (clearTimeout(timer.current), refresh())} setNotice={setNotice} />
      <OnlineSection sources={of("url", "crawl")} add={add} remove={remove} retry={retry} />
      {driveOn && drive && <DrivePanel api={api} agentId={agentId} sources={of("google_drive").filter((s) => !(s.spec as { registrationId?: string }).registrationId)} refresh={async () => (clearTimeout(timer.current), refresh())} state={drive} reload={loadDrive} />}
      {folders && (folders.enabled || folders.folders.length > 0) && (
        <DriveFolders api={api} agentId={agentId} sources={of("google_drive").filter((s) => !!(s.spec as { registrationId?: string }).registrationId)} refresh={async () => (clearTimeout(timer.current), refresh())} enabled={folders.enabled} shareWith={folders.shareWith} folders={folders.folders} reload={loadFolders} />
      )}
      <WorkspaceSection sources={of(...(driveOn ? [] : ["google_drive"]), "microsoft365", "azure_blob").filter((s) => !(s.type === "google_drive" && (s.spec as { registrationId?: string }).registrationId))} add={add} remove={remove} driveOn={driveOn} />
      <TextSection sources={of("text")} add={add} remove={remove} retry={retry} />
    </div>
  );
}

type AddFn = (b: Record<string, unknown>) => Promise<Source | null>;
type RowFn = (s: Source) => void | Promise<void>;

export function SourceList({ sources, remove, retry }: { sources: Source[]; remove: RowFn; retry?: RowFn }) {
  if (!sources.length) return null;
  return (
    <ul className="sources" aria-label="Sources">
      {sources.map((s) => {
        const st = STATUS[s.status];
        return (
          <li key={s.id} data-status={s.status}>
            <div className="row between">
              <span className="source-name">{s.name}</span>
              <span className="row">
                <Badge tone={st.tone}>{s.status === "unsupported" && s.type === "file" ? "Not supported" : st.label}</Badge>
                {s.status === "indexed" && <span className="hint">{s.chunks} passage{s.chunks === 1 ? "" : "s"}</span>}
                {retry && (s.status === "error" || (s.sync && s.status === "indexed")) && (
                  <button className="btn ghost" onClick={() => void retry(s)} aria-label={`${s.status === "error" ? "Retry" : "Sync now"} ${s.name}`}>
                    {s.status === "error" ? "Retry" : "Sync now"}
                  </button>
                )}
                <button className="btn ghost" onClick={() => void remove(s)} aria-label={`Remove ${s.name}`}>
                  Remove
                </button>
              </span>
            </div>
            {s.error && <p className={s.status === "error" ? "error" : "hint"}>{s.error}</p>}
            {s.sync && (
              <p className="hint">
                {s.sync.paused
                  ? `Updates are paused${s.sync.pauseReason ? `: ${s.sync.pauseReason}` : "."} Choose Retry to start again.`
                  : `Updates ${cadence(s.sync.cron)}${when(s.sync.lastRunAt) ? ` · last updated ${when(s.sync.lastRunAt)}` : ""}.`}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function OnboardingSection({ sources, add, remove, retry, loaded }: { sources: Source[]; add: AddFn; remove: RowFn; retry: RowFn; loaded: boolean }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  return (
    <Section title="Onboarding details" help="Short facts about your business that the agent should always know: who you are, where you are, what you sell.">
      <TextInput label="Title" value={title} onChange={setTitle} placeholder="e.g. About GoNemo" />
      <TextInput label="Description" multiline rows={3} value={description} onChange={setDescription} placeholder="GoNemo has been building chatbot agents since 2023…" />
      <button
        className="btn secondary"
        disabled={!title.trim() || !description.trim()}
        onClick={async () => {
          if (await add({ type: "onboarding", title: title.trim(), description: description.trim() })) {
            setTitle("");
            setDescription("");
          }
        }}
      >
        Add details
      </button>
      {loaded && <SourceList sources={sources} remove={remove} retry={retry} />}
    </Section>
  );
}

function OnlineSection({ sources, add, remove, retry }: { sources: Source[]; add: AddFn; remove: RowFn; retry: RowFn }) {
  const [url, setUrl] = useState("");
  const [crawl, setCrawl] = useState(false);
  const [cron, setCron] = useState(SCHEDULES[0]!.cron);
  return (
    <Section title="Websites" help="Add one page, or let the agent read a whole website. It reads the site again on a schedule, so answers stay up to date. Pages you add must be public.">
      <TextInput label="Web address" type="url" value={url} onChange={setUrl} placeholder="https://example.com/faq" />
      <label className="inline">
        <input type="checkbox" checked={crawl} onChange={(e) => setCrawl(e.target.checked)} /> Read the whole website (follows links)
      </label>
      <Field label="Update" htmlFor="site-cron">
        <select id="site-cron" value={cron} onChange={(e) => setCron(e.target.value)}>
          {SCHEDULES.map((s) => (
            <option key={s.cron} value={s.cron}>
              {s.label}
            </option>
          ))}
        </select>
      </Field>
      <button className="btn secondary" disabled={!url.trim()} onClick={async () => (await add({ type: crawl ? "crawl" : "url", url: url.trim(), syncCron: cron })) && setUrl("")}>
        Add website
      </button>
      <SourceList sources={sources} remove={remove} retry={retry} />
    </Section>
  );
}

function WorkspaceSection({ sources, add, remove, driveOn }: { sources: Source[]; add: AddFn; remove: RowFn; driveOn?: boolean }) {
  const [kind, setKind] = useState<"google_drive" | "microsoft365" | "azure_blob">(driveOn ? "azure_blob" : "google_drive");
  const [folder, setFolder] = useState("");
  const [azure, setAzure] = useState({ account: "", container: "", prefix: "" });
  const submit = async () => {
    const ok = kind === "azure_blob" ? await add({ type: kind, ...azure }) : await add({ type: kind, folderUrl: folder.trim() });
    if (ok) {
      setFolder("");
      setAzure({ account: "", container: "", prefix: "" });
    }
  };
  return (
    <Section title="Connect to workspaces" help="Keep the agent in sync with a folder. These connectors are coming soon; you can save the address now.">
      <Field label="Workspace" htmlFor="ws-kind">
        <select id="ws-kind" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          {!driveOn && <option value="google_drive">Google Drive</option>}
          <option value="azure_blob">Azure Blob Storage</option>
        </select>
      </Field>
      {kind === "azure_blob" ? (
        <div className="grid3">
          <TextInput label="Storage account" value={azure.account} onChange={(v) => setAzure({ ...azure, account: v })} />
          <TextInput label="Container" value={azure.container} onChange={(v) => setAzure({ ...azure, container: v })} />
          <TextInput label="Folder (optional)" value={azure.prefix} onChange={(v) => setAzure({ ...azure, prefix: v })} />
        </div>
      ) : (
        <TextInput label="Folder link" type="url" value={folder} onChange={setFolder} placeholder="https://…" />
      )}
      <button className="btn secondary" disabled={kind === "azure_blob" ? !azure.account || !azure.container : !folder.trim()} onClick={submit}>
        Save connection
      </button>
      <SourceList sources={sources} remove={remove} />
    </Section>
  );
}

function TextSection({ sources, add, remove, retry }: { sources: Source[]; add: AddFn; remove: RowFn; retry: RowFn }) {
  const [text, setText] = useState("");
  return (
    <Section title="Train with text" help="Paste anything the agent should know: policies, product descriptions, prices.">
      <TextInput label="Text" multiline rows={6} value={text} onChange={setText} />
      <button className="btn secondary" disabled={!text.trim()} onClick={async () => (await add({ type: "text", text: text.trim() })) && setText("")}>
        Train with this text
      </button>
      <SourceList sources={sources} remove={remove} retry={retry} />
    </Section>
  );
}
